/**
 * View-specific vehicle types for list views and cards.
 * These types use nested objects to match the API response structure
 * for vehicle list endpoints, which differs from the flat Vehicle type
 * used in @/apis/vehicle/vehicle-apis for single vehicle operations.
 */

export interface VehicleStation {
  id: number;
  code?: string;
  name?: string;
}

export interface VehicleCity {
  id: number;
  name?: string;
}

export interface VehicleLock {
  externalId?: string;
  code?: string;
  battery?: number;
  open?: boolean;
  additionalCode?: string;
  brand?: string;
}

export interface VehicleDock {
  code?: string;
}

export interface VehicleLinkedStation {
  id: number;
}

/**
 * View-specific vehicle type for list views and cards.
 * Uses nested objects for related entities (station, city, lock, dock)
 * as returned by the vehicle list API endpoint.
 */
export interface VehicleListItem {
  id: number;
  code: string;
  name: string;
  status: string;
  type: string;
  brand: string;
  battery: number | null;
  latitude: number;
  longitude: number;
  lastSeenOnline?: string;
  googleMapLink?: string;
  userId?: number;
  journeyId?: number;
  station?: VehicleStation | null;
  linkedStation?: VehicleLinkedStation | null;
  city?: VehicleCity | null;
  lock?: VehicleLock | null;
  dock?: VehicleDock | null;
}

export interface VehicleUpdateBody {
  name: string;
  status: string;
  stationId?: number;
  linkedStationId?: number;
  latitude: number;
  longitude: number;
  cityId?: number;
  code: string;
  type: string;
  lockExternalId?: string;
  lockAdditionalCode?: string;
  battery?: number | null;
  [key: string]: unknown;
}

/** @deprecated Use VehicleListItem instead */
export type Bike = VehicleListItem;

/** @deprecated Use VehicleUpdateBody instead */
export type BikeUpdateBody = VehicleUpdateBody;

/**
 * Lightweight station reference for select options in modals.
 */
export interface StationOption {
  id: string | number;
  name: string;
}

/**
 * Vehicle data structure for edit modals.
 * Uses flexible id type to support both new and existing vehicles.
 */
export interface VehicleEditData {
  id: string | number;
  name?: string;
  status?: string;
  code?: string;
  type?: string;
  brand?: string;
  battery?: number;
  latitude?: number;
  longitude?: number;
  city?: { id: string | number; name: string };
  station?: StationOption;
  linkedStation?: StationOption;
  lock?: { brand?: string; externalId?: string; additionalCode?: string };
  partnerId?: number | null;
}

/**
 * Vehicle data for QR code modal.
 */
export interface VehicleQrData {
  code?: string;
  name?: string;
  city?: { id?: number } | null;
}

/**
 * QR code response from the API.
 */
export interface QrCodeResponse {
  has_qr_code: boolean;
  qr_code_base64?: string;
  qr_url?: string;
  error?: string;
}

/**
 * Input type for vehicle transformation.
 * Matches the Vehicle type from @/v2/apis/vehicle/vehicle-apis
 */
interface VehicleInput {
  id: number;
  code?: string;
  name?: string;
  status?: string;
  type?: string;
  brand?: string;
  battery?: number;
  latitude?: number;
  longitude?: number;
  city?: { id: number; name?: string } | null;
  station?: { id: number; name?: string } | null;
  linkedStation?: { id: number } | null;
  lock?: VehicleLock | null;
  partnerId?: number | null;
}

/**
 * Transform a Vehicle API response to VehicleEditData for edit modals.
 * Handles the nested object structure (city, station, linkedStation, lock)
 * and converts it to the format expected by edit modal forms.
 */
export function toVehicleEditData(
  vehicle: VehicleInput | null | undefined
): VehicleEditData | null {
  if (!vehicle) return null;

  return {
    id: vehicle.id,
    name: vehicle.name,
    status: vehicle.status,
    code: vehicle.code,
    type: vehicle.type,
    brand: vehicle.brand,
    battery: vehicle.battery,
    latitude: vehicle.latitude,
    longitude: vehicle.longitude,
    city: vehicle.city
      ? { id: vehicle.city.id, name: vehicle.city.name ?? "" }
      : undefined,
    station: vehicle.station
      ? { id: vehicle.station.id, name: vehicle.station.name ?? "" }
      : undefined,
    linkedStation: vehicle.linkedStation
      ? { id: vehicle.linkedStation.id, name: "" }
      : undefined,
    lock: vehicle.lock ?? { brand: vehicle.brand },
    partnerId: vehicle.partnerId ?? null,
  };
}
